import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers/validateFormData'
import { Button, IButtonProps } from 'core-components/library/button'
import { Loader, LoaderOverlay } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { Controller } from 'core-components/library/validation'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { fail, success } from 'onboarding/classes/services/notification'

import { REQUESTOR_FILL_APJ_FORM_KEY } from '../configs'
import { getInitialRequestorFillForm } from '../helpers/getInitialRequestorFillForm'
import { mapRequestorFillDBToForm } from '../helpers/mapRequestorFillDBToForm'
import { mapRequestorFillFormToDB } from '../helpers/mapRequestorFillFormToDB'
import { useRequestorFillData } from '../hooks/useRequestorFillData'
import { useTerritoryOfSaleRootAPJ } from '../hooks/useTerritoryOfSaleRootAPJ'
import { TRequestorFill, TRequestorFillForm } from '../models'
import styles from '../RequestorFill.module.scss'
import { FormFields } from '../requestorFillAPJ/FormFields'

interface IProps {
  requestId: string
  audienceSettings: IAudienceCondition
  disabled?: boolean
}

export const RequestorFillAPJ = ({ requestId, audienceSettings, disabled }: IProps) => {
  const dispatch = useAppDispatch()
  const [defaultFormData, setDefaultFormData] = useState(getInitialRequestorFillForm)
  const [fetchedRequestor, setFetchedRequestor] = useState<TRequestorFill | null>(null)
  const [{ formData, modifiedFields }] = useGetFormData<TRequestorFillForm>(
    REQUESTOR_FILL_APJ_FORM_KEY
  )
  const isFormChanged = useMemo(() => Boolean(modifiedFields.length), [modifiedFields])

  const { cruds, isLoading, isSaving, onSave, partner, partnerTerritory } = useRequestorFillData({
    setItem: setFetchedRequestor,
    requestId,
    type: 'APJ',
  })
  const territoryOfSaleRoot = useTerritoryOfSaleRootAPJ(partner.Country || '')

  const onHandleSave = useCallback(async () => {
    const errors = await validateFormData(formData, REQUESTOR_FILL_APJ_FORM_KEY)
    if (!errors.length && isFormChanged) {
      const result = await onSave(mapRequestorFillFormToDB({ ...defaultFormData, ...formData }))
      if (result) {
        setDefaultFormData(mapRequestorFillDBToForm(result))
        success('Saved')
      } else {
        fail('Failed to save')
      }
    }
  }, [isFormChanged, onSave, formData, defaultFormData])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(REQUESTOR_FILL_APJ_FORM_KEY))
    }
  }, [])

  useEffect(() => {
    if (fetchedRequestor) {
      setDefaultFormData(mapRequestorFillDBToForm(fetchedRequestor))
    }
  }, [fetchedRequestor])

  return isLoading ? (
    <Loader />
  ) : (
    <Controller>
      <div className={styles.content}>
        <LoaderOverlay show={isSaving} />
        <FormFields
          formKey={REQUESTOR_FILL_APJ_FORM_KEY}
          cruds={cruds}
          partnerTerritory={partnerTerritory}
          hpSigningEntity={defaultFormData.HPSigningEntity}
          territoryOfSaleSelected={defaultFormData.TerritorySaleISO}
          territoryOfSaleRoot={territoryOfSaleRoot}
          dealValue={defaultFormData.DealValue}
          dealMargin={defaultFormData.DealMargin}
          winProbability={defaultFormData.WinProbability}
          salesStage={defaultFormData.SalesStage}
          writeAudience={audienceSettings}
          isDisabled={disabled}
        />

        <div className={styles.btnWrapper}>
          <Tooltip
            tipContent={() => <span>No changes to save</span>}
            disabled={isFormChanged || disabled}
          >
            <VisualAudienceExtended<IButtonProps>
              write={audienceSettings}
              component={Button}
              onClick={onHandleSave}
              disabled={!isFormChanged || disabled}
            >
              Save
            </VisualAudienceExtended>
          </Tooltip>
        </div>
      </div>
    </Controller>
  )
}
